import type { ReactNode } from 'react';

interface Benefit {
  icon: React.FC<{ size?: number; className?: string }>;
  title: string;
  text: string;
}

interface PdfLayoutProps {
  category: string;
  headline: string;
  headlineHighlight: string;
  description: string;
  accentColor: string;
  benefits: Benefit[];
  children: ReactNode;
}

export function PdfLayout({
  category,
  headline,
  headlineHighlight,
  description,
  accentColor,
  benefits,
  children,
}: PdfLayoutProps) {
  return (
    <div className="pdf-page bg-white relative overflow-hidden flex flex-col">
      {/* Background accent blobs */}
      <div
        className="pdf-blob-decoration absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full blur-[100px] pointer-events-none"
        style={{ backgroundColor: accentColor, opacity: 0.08 }}
      />
      <div
        className="pdf-blob-decoration absolute -bottom-20 -left-20 w-[200px] h-[200px] rounded-full blur-[80px] pointer-events-none"
        style={{ backgroundColor: accentColor, opacity: 0.06 }}
      />

      {/* Header */}
      <header className="flex items-center justify-between px-10 pt-8 pb-4 relative z-10 shrink-0">
        <img src="/scada-ink-logo.png" alt="Scada.Ink" className="h-8" />
        <span
          className="text-xs font-medium px-3 py-1 rounded-full"
          style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
        >
          {category}
        </span>
      </header>

      {/* Divider */}
      <div className="mx-10 h-px bg-glass-border relative z-10 shrink-0" />

      {/* Content — flex-1 so it fills between header and footer, overflow hidden */}
      <div className="px-10 pt-6 pb-4 relative z-10 flex-1 min-h-0 overflow-hidden">
        <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-text-primary">
          {headline}{' '}
          <span style={{ color: accentColor }}>
            {headlineHighlight}
          </span>
        </h1>
        <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-lg">
          {description}
        </p>

        {/* Two-column: benefits + visual */}
        <div className="mt-6 grid grid-cols-2 gap-8">
          <div className="space-y-4">
            {benefits.map((b) => (
              <div key={b.title} className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                  style={{ backgroundColor: `${accentColor}12`, color: accentColor }}
                >
                  <b.icon size={16} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">{b.title}</h3>
                  <p className="text-xs text-text-secondary leading-relaxed mt-0.5">{b.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center">
            {children}
          </div>
        </div>
      </div>

      {/* Factory floor illustration — washed out, edge to edge */}
      <div className="absolute bottom-10 left-0 right-0 pointer-events-none z-0" style={{ opacity: 0.035 }}>
        <FactoryFloor />
      </div>

      {/* Footer */}
      <footer className="px-10 py-3 flex items-center justify-between border-t border-glass-border bg-white relative z-10 shrink-0">
        <span className="text-xs text-text-muted">
          Scada.Ink &mdash; Industrial Monitoring &amp; Analytics
        </span>
        <span className="text-[10px] text-text-muted">
          &copy; 2026 Scada.Ink. All rights reserved.
        </span>
        <span className="text-xs font-medium text-brand-primary">
          scada.ink
        </span>
      </footer>
    </div>
  );
}

/** Washed-out factory floor silhouette — SVG edge-to-edge at bottom of page */
function FactoryFloor() {
  return (
    <svg viewBox="0 0 816 200" className="w-full" preserveAspectRatio="none">
      {/* Floor line */}
      <line x1="0" y1="170" x2="816" y2="170" stroke="#1e293b" strokeWidth="2" />

      {/* CNC Machine 1 */}
      <rect x="30" y="90" width="80" height="80" rx="4" fill="#1e293b" />
      <rect x="35" y="95" width="30" height="20" rx="2" fill="#64748b" />
      <rect x="45" y="130" width="55" height="40" rx="2" fill="#334155" />
      <rect x="70" y="95" width="35" height="30" rx="2" fill="#475569" />

      {/* Conveyor belt */}
      <rect x="130" y="150" width="120" height="20" rx="4" fill="#1e293b" />
      <circle cx="140" cy="170" r="8" fill="#334155" />
      <circle cx="140" cy="170" r="4" fill="#64748b" />
      <circle cx="240" cy="170" r="8" fill="#334155" />
      <circle cx="240" cy="170" r="4" fill="#64748b" />
      {/* Conveyor items */}
      <rect x="155" y="142" width="12" height="10" rx="1" fill="#475569" />
      <rect x="180" y="142" width="12" height="10" rx="1" fill="#475569" />
      <rect x="205" y="142" width="12" height="10" rx="1" fill="#475569" />

      {/* Robot arm */}
      <rect x="280" y="140" width="20" height="30" rx="2" fill="#1e293b" />
      <line x1="290" y1="140" x2="310" y2="100" stroke="#1e293b" strokeWidth="6" strokeLinecap="round" />
      <line x1="310" y1="100" x2="340" y2="85" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
      <line x1="340" y1="85" x2="355" y2="95" stroke="#1e293b" strokeWidth="4" strokeLinecap="round" />
      <circle cx="310" cy="100" r="5" fill="#475569" />
      <circle cx="340" cy="85" r="4" fill="#475569" />

      {/* Large machine / press */}
      <rect x="380" y="60" width="70" height="110" rx="4" fill="#1e293b" />
      <rect x="385" y="65" width="60" height="35" rx="2" fill="#334155" />
      <rect x="395" y="110" width="40" height="50" rx="2" fill="#475569" />
      <circle cx="415" cy="82" r="10" fill="#475569" />

      {/* Control panel */}
      <rect x="470" y="100" width="40" height="70" rx="3" fill="#1e293b" />
      <rect x="475" y="105" width="30" height="18" rx="2" fill="#3b82f6" opacity="0.3" />
      <circle cx="485" cy="135" r="3" fill="#64748b" />
      <circle cx="495" cy="135" r="3" fill="#64748b" />
      <circle cx="485" cy="148" r="3" fill="#16a34a" opacity="0.5" />

      {/* CNC Machine 2 */}
      <rect x="530" y="80" width="90" height="90" rx="4" fill="#1e293b" />
      <rect x="535" y="85" width="40" height="25" rx="2" fill="#475569" />
      <rect x="540" y="120" width="70" height="45" rx="2" fill="#334155" />
      <circle cx="600" cy="97" r="8" fill="#334155" />

      {/* Shelving / storage rack */}
      <rect x="640" y="70" width="50" height="100" fill="none" stroke="#1e293b" strokeWidth="3" />
      <line x1="640" y1="100" x2="690" y2="100" stroke="#1e293b" strokeWidth="2" />
      <line x1="640" y1="130" x2="690" y2="130" stroke="#1e293b" strokeWidth="2" />
      <rect x="645" y="75" width="15" height="20" rx="1" fill="#475569" />
      <rect x="665" y="78" width="15" height="17" rx="1" fill="#475569" />
      <rect x="645" y="105" width="18" height="20" rx="1" fill="#475569" />
      <rect x="668" y="105" width="12" height="20" rx="1" fill="#475569" />
      <rect x="645" y="135" width="15" height="25" rx="1" fill="#475569" />

      {/* Forklift silhouette */}
      <rect x="720" y="140" width="40" height="25" rx="3" fill="#1e293b" />
      <rect x="715" y="120" width="8" height="50" rx="1" fill="#1e293b" />
      <rect x="710" y="110" width="14" height="12" rx="1" fill="#334155" />
      <circle cx="728" cy="170" r="7" fill="#334155" />
      <circle cx="728" cy="170" r="3" fill="#64748b" />
      <circle cx="752" cy="170" r="7" fill="#334155" />
      <circle cx="752" cy="170" r="3" fill="#64748b" />
      <rect x="725" y="135" width="25" height="8" rx="2" fill="#475569" />

      {/* Ceiling beams */}
      <rect x="0" y="20" width="816" height="4" fill="#1e293b" opacity="0.3" />
      <rect x="100" y="20" width="4" height="30" fill="#1e293b" opacity="0.2" />
      <rect x="300" y="20" width="4" height="30" fill="#1e293b" opacity="0.2" />
      <rect x="500" y="20" width="4" height="30" fill="#1e293b" opacity="0.2" />
      <rect x="700" y="20" width="4" height="30" fill="#1e293b" opacity="0.2" />

      {/* Overhead lights */}
      <rect x="190" y="24" width="20" height="6" rx="1" fill="#ca8a04" opacity="0.15" />
      <rect x="400" y="24" width="20" height="6" rx="1" fill="#ca8a04" opacity="0.15" />
      <rect x="600" y="24" width="20" height="6" rx="1" fill="#ca8a04" opacity="0.15" />
    </svg>
  );
}
